/** A record header's Actions menu (plan §3) — the record's actions in groups, e.g. a
 *  booking's **Record** (the record-changing actions, the same handlers as the panels'
 *  controls — queued in edit mode, "… now" while the draft is clean), then its immediate
 *  actions — Communication · <Restech> · Cache · Diagnose. A group with no item is not
 *  drawn. The kill switch's note (`EDITING_OFF`, "Editing is switched off") leads it; with
 *  neither items nor a note it is not drawn — an empty menu advertises nothing. An item's handler gets the Actions
 *  button, where a confirmation or a picker it opens hangs once the menu has closed.
 *
 *  An item the operator may run but not right now (a dirty draft, a booking its restech
 *  cannot be asked for) stays in the menu, disabled, and says why on its second line —
 *  still reachable by keyboard so the reason is read out.
 *
 *  An item that opens another app's page (the record in the dashboard) is a real link
 *  (`href`): a menu item drawn as an anchor that opens a new tab, cmd-clickable, never a
 *  `window.open` from a handler.
 *
 *  An item that happens on the server at once, not in the draft (`immediate`: "Publish now",
 *  "Regenerate deep link now") carries the bolt as its list icon and says so to assistive tech
 *  ("happens at once, not part of the draft" — `ImmediateActionButton`'s words); the other
 *  items of its group keep the icon's room, so the labels stay aligned (the Draft-Or-Now
 *  Rule). */
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Divider, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import { useId, useRef, useState, type ReactNode } from 'react';

import { IMMEDIATE_DESCRIPTION } from '@/features/records/actions/ImmediateActionButton';

/** One item: an action run in place (`onSelect`), or another app's page opened in a new tab
 *  (`href`) — one of the two. */
export interface RecordActionItem {
  key: string;
  label: string;
  /** Muted hint on the right ("adds to draft", "background task"). */
  hint?: string;
  /** A second line under the label — on a disabled item, why it is disabled. */
  description?: string;
  disabled?: boolean;
  /** Destructive or irreversible (soft-delete, anonymize): its label in Signal Red. */
  danger?: boolean;
  /** Happens on the server at once, not in the draft: the bolt icon and its words. */
  immediate?: boolean;
  /** `anchorEl` — the Actions button. */
  onSelect?: (anchorEl: HTMLElement | null) => void;
  /** An absolute URL: the item is a link that opens it in a new tab
   *  (`rel="noopener noreferrer"`), cmd-clickable. */
  href?: string;
}

/** A link item's anchor: a new tab, without the opener or the referrer. */
const linkProps = (href: string) =>
  ({ component: 'a', href, target: '_blank', rel: 'noopener noreferrer' }) as const;

export interface RecordActionGroup {
  key: string;
  label: string;
  items: RecordActionItem[];
}

export function RecordActionsMenu({ groups, note }: { groups: RecordActionGroup[]; note?: ReactNode }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const id = useId();
  const visible = groups.filter((g) => g.items.length > 0);
  if (visible.length === 0 && !note) return null;
  const close = () => setAnchor(null);
  return (
    <>
      <Button
        ref={buttonRef}
        variant="outlined"
        size="small"
        endIcon={<ExpandMoreIcon />}
        aria-haspopup="menu"
        aria-expanded={anchor ? true : undefined}
        aria-controls={anchor ? id : undefined}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        Actions
      </Button>
      <Menu
        id={id}
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 280, maxWidth: 360 } }, list: { disabledItemsFocusable: true } }}
      >
        {note && (
          <Box
            role="note"
            sx={{ px: 2, py: 1, display: 'flex', gap: 1, alignItems: 'flex-start', color: 'text.secondary' }}
          >
            <InfoOutlinedIcon aria-hidden sx={{ fontSize: 16, mt: '2px' }} />
            <Typography variant="body2">{note}</Typography>
          </Box>
        )}
        {visible.flatMap((g, gi) => [
          ...(gi > 0 || note ? [<Divider key={`${g.key}:rule`} />] : []),
          <ListSubheader key={`${g.key}:label`} sx={{ lineHeight: '30px', fontWeight: 600 }}>
            {g.label}
          </ListSubheader>,
          ...g.items.map((it) => (
            <MenuItem
              key={`${g.key}:${it.key}`}
              disabled={it.disabled}
              data-action={it.key}
              {...(it.href ? linkProps(it.href) : {})}
              {...(it.immediate ? ({ 'aria-description': IMMEDIATE_DESCRIPTION } as Record<string, string>) : {})}
              onClick={() => {
                close();
                // A link item opens its page itself (the anchor's default).
                it.onSelect?.(buttonRef.current);
              }}
              sx={{
                gap: 2,
                alignItems: 'flex-start',
                whiteSpace: 'normal',
                // A disabled item stays legible: its label dims, its reason does not.
                '&.Mui-disabled': { opacity: 1 },
                '&.Mui-disabled [data-part="label"]': { color: 'text.disabled' },
              }}
            >
              {g.items.some((x) => x.immediate) && (
                <Box component="span" aria-hidden sx={{ display: 'inline-flex', width: 18, flex: 'none', mt: '3px' }}>
                  {it.immediate && <BoltOutlinedIcon sx={{ fontSize: 18, color: it.danger ? 'error.main' : 'text.secondary' }} />}
                </Box>
              )}
              <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                <Box component="span" data-part="label" sx={{ display: 'block', ...(it.danger ? { color: 'error.main' } : {}) }}>
                  {it.label}
                </Box>
                {it.description && (
                  <Typography variant="caption" component="span" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                    {it.description}
                  </Typography>
                )}
              </Box>
              {it.hint && (
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', lineHeight: '24px' }}>
                  {it.hint}
                </Typography>
              )}
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
}
